import { FormikProvider, useFormik } from 'formik';
import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';

import { addressHasErrors } from '@perpay-web/components/base/AddressInput/AddressInput';
import { useAddressInputContext } from '@perpay-web/components/base/AddressInput/useAddressInputContext';
import BackButton from '@perpay-web/components/base/BackButton/BackButton';
import CustomButton from '@perpay-web/components/base/CustomButton/CustomButton';
import EmailMisspelledModal from '@perpay-web/components/composite/EmailMisspelledModal/EmailMisspelledModal';
import FocusContent from '@perpay-web/components/composite/FocusLayout/FocusContent';
import FocusFooter from '@perpay-web/components/composite/FocusLayout/FocusFooter';
import FocusHeader from '@perpay-web/components/composite/FocusLayout/FocusHeader';
import FocusLayout from '@perpay-web/components/composite/FocusLayout/FocusLayout';
import Stack from '@perpay-web/components/design-system/Stack/Stack';
import CardCta from '@perpay-web/fintech/components/base/CardCta/CardCta';
import CustomLink from '@perpay-web/fintech/components/base/CustomLink/CustomLink';
import CardApplicationHeader from '@perpay-web/fintech/components/base/Header/CardApplicationHeader';
import CardApplicationConfirmAddress from '@perpay-web/fintech/components/composite/CardApplicationConfirmAddress/CardApplicationConfirmAddress';
import CardApplicationPersonalDetailsForm from '@perpay-web/fintech/components/composite/CardApplicationPersonalDetailsForm/CardApplicationPersonalDetailsForm';
import IneligibleStateModal from '@perpay-web/fintech/components/composite/IneligibleStateModal/IneligibleStateModal';
import PartnerHostedOnboardingValueProp from '@perpay-web/fintech/components/composite/PartnerHostedOnboardingValueProp/PartnerHostedOnboardingValueProp';
import PartnerOnboardingExistingUserModal from '@perpay-web/fintech/components/composite/PartnerOnboardingExistingUserModal/PartnerOnboardingExistingUserModal';
import statesIneligibleForCard from '@perpay-web/fintech/constants/statesIneligibleForCardCardSignupMVP';
import {
    PERPAY_TERMS_AND_CONDITIONS_URL,
    REWARDS_PROGRAM_URL,
} from '@perpay-web/fintech/constants/urls';
import { useCardApplicationConfirmAddressModalContext } from '@perpay-web/fintech/hooks/useCardApplicationConfirmAddressModalContext';
import { useEmailMisspelledModalContext } from '@perpay-web/fintech/hooks/useEmailMispelledModalContext';
import useEmailMisspelled from '@perpay-web/fintech/hooks/useEmailMisspelled';
import { useFullHeader } from '@perpay-web/fintech/hooks/useFullHeaderContext';
import { usePartnerOnboardingExistingUserModalContext } from '@perpay-web/fintech/hooks/usePartnerOnboardingExistingUserModalContext';
import { usePartnerOnboardingSignupIneligibleStateModalContext } from '@perpay-web/fintech/hooks/usePartnerOnboardingSignupIneligibleStateModalContext';
import { paths } from '@perpay-web/fintech/props/appPaths';
import {
    addressSchema,
    cardPersonalDetailsExistingUserSchema,
    cardPersonalDetailsSchema,
} from '@perpay-web/fintech/validators/formBased';
import { useIsMobile } from '@perpay-web/hooks/useIsMobile';
import { EndToEndTestIdProvider } from '@perpay-web/utils/endToEndTestIds';
import { noop } from '@perpay-web/utils/noop';
import { getAnchorTargetFromUserAgent } from '@perpay-web/utils/routeUtils';
import { validateForm } from '@perpay-web/validators/formBased';

import styles from './CardApplicationPersonalDetails.scss';

const CreditScoreDisaclaimer = () => (
    <div className={styles.disclaimer__score}>
        <p className={styles['disclaimer__bank-note']}>
            The Perpay Credit Card is issued by Celtic Bank. The Perpay Credit
            Card is subject to credit approval. Not available to residents of
            New Hampshire.
        </p>
        <sup className={styles['disclaimer-footnote']}>1</sup>
        See our{' '}
        <CustomLink
            action='inline-link'
            href={REWARDS_PROGRAM_URL}
            target={getAnchorTargetFromUserAgent()}
            className={styles.disclaimer__link}
        >
            Rewards Agreement
        </CustomLink>{' '}
        for more details on the Perpay Credit Card rewards program.&nbsp;
        <sup className={styles['disclaimer-footnote']}>2</sup>
        Applying for a Perpay Credit Card will not impact your FICO® or
        Vantage® scores.
    </div>
);

const DisclaimerSection = ({ partnerCode }) =>
    partnerCode ? (
        <div className={styles.disclaimer__terms}>
            By clicking &apos;Continue&apos; you agree to Perpay&apos;s{' '}
            <CustomLink
                action='inline-link'
                href={PERPAY_TERMS_AND_CONDITIONS_URL}
                target={getAnchorTargetFromUserAgent()}
                className={styles.disclaimer__link}
            >
                Terms&nbsp;and&nbsp;Conditions
            </CustomLink>
            .
        </div>
    ) : null;

const TitleSection = () => (
    <Stack className={styles['small-stack']}>
        <div className={styles.title}>Apply now!</div>
        <div className={styles.subtitle}>
            Applying takes 60 seconds and{' '}
            <span className={styles['subtitle--bold']}>
                will not impact your credit score.
                <sup className={styles['disclaimer-footnote']}>2</sup>
            </span>
        </div>
    </Stack>
);

const CardApplicationPersonalDetails = ({
    onSubmit,
    errors = {},
    partnerCode = null,
    welcomeOffer = null,
    isUserInPremiumGroup = false,
    isUserIdentityVerified = false,
    isExistingUserWithPassword = false,
    dateOfBirth = '',
    firstName = '',
    lastName = '',
    ssn = '',
    email = '',
    phoneNumber = '',
    isExistingUser,
    initialAddressValues = {
        street1: '',
        street2: '',
        city: '',
        state: '',
        zipcode: '',
    },
    onIneligibleStateAddress = noop,
    analyticsOnOpenIneligibleStateModal,
    analyticsOnCloseIneligibleStateModal,
    analyticsOnEditAddressIneligibleStateModal,
    onEmailMisspelled,
    onConfirmAddressModalClose = noop,
    onConfirmAddressModalContinue = noop,
    onErrorStateChange = noop,
}) => {
    const { setShowAddressSuggestions } = useAddressInputContext();
    const { isModalOpen, setIsModalOpen } =
        usePartnerOnboardingSignupIneligibleStateModalContext();
    const {
        isModalOpen: isConfirmAddressModalOpen,
        setIsModalOpen: setIsConfirmAddressModalOpen,
    } = useCardApplicationConfirmAddressModalContext();

    // Reset loading state when errors occur
    // This ensures the submit button becomes clickable again after form submission errors
    useEffect(() => {
        const hasErrors = errors && Object.keys(errors).length > 0;
        onErrorStateChange(hasErrors);
    }, [errors, onErrorStateChange]);
    const {
        isAnimatedModalOpen: isExistingUserModalOpen,
        setIsAnimatedModalOpen: setIsExistingUserModalOpen,
        animatedModalClassName,
        existingUserFirstName,
        matchMethod,
    } = usePartnerOnboardingExistingUserModalContext();
    const navigate = useNavigate();
    const { isMobile } = useIsMobile();
    const { suggestions: emailSuggestions } = useEmailMisspelled();
    const {
        isModalOpen: isEmailModalOpen,
        setIsModalOpen: setIsEmailModalOpen,
    } = useEmailMisspelledModalContext();

    const onBackClicked = useCallback(() => {
        navigate(paths.dashboard.path);
    }, [navigate]);

    const renderHeaderCallback = useCallback(
        () => (
            <CardApplicationHeader
                partnerCode={partnerCode}
                onBackClicked={
                    isMobile && isExistingUserWithPassword
                        ? onBackClicked
                        : null
                }
            />
        ),
        [partnerCode, isMobile, isExistingUserWithPassword, onBackClicked],
    );
    useFullHeader(renderHeaderCallback);

    const processSubmit = useCallback(
        (values) => {
            if (phoneNumber) {
                onSubmit(values);
            } else {
                const { phoneNumber: extractedPhoneNumber, ...otherValues } =
                    values;
                const transformedValues = {
                    ...otherValues,
                    mobilePhone: `+1${extractedPhoneNumber}`,
                };
                onSubmit(transformedValues);
            }
        },
        [onSubmit, phoneNumber],
    );

    const handleSubmit = (values, formikBag) => {
        if (statesIneligibleForCard.includes(values.state)) {
            onIneligibleStateAddress();
            return;
        }

        if (emailSuggestions.length) {
            onEmailMisspelled();
            return;
        }

        processSubmit(values);
        // Keep submitting state true - will be reset by error handler or navigation
        // Using setTimeout to ensure it persists after Formik's internal reset
        setTimeout(() => formikBag.setSubmitting(true), 0);
    };

    const getValidationSchema = () => {
        if (isUserIdentityVerified) {
            return addressSchema;
        }
        if (isExistingUser) {
            return cardPersonalDetailsExistingUserSchema;
        }
        return cardPersonalDetailsSchema;
    };

    const getInitialValues = () => {
        const existingUserInitialValues = {
            ...initialAddressValues,
            firstName,
            lastName,
        };
        const newUserInitialValues = {
            ...existingUserInitialValues,
            email,
            phoneNumber,
        };

        if (isUserIdentityVerified) {
            return { ...initialAddressValues };
        }
        if (isExistingUser) {
            return existingUserInitialValues;
        }
        return newUserInitialValues;
    };

    const formik = useFormik({
        initialValues: getInitialValues(),
        onSubmit: handleSubmit,
        validate: (values) => validateForm(values, getValidationSchema()),
        validateOnMount:
            getInitialValues().street1 && getInitialValues().street1.length > 0,
    });

    useEffect(() => {
        if (
            ((errors && Object.keys(errors).length > 0) ||
                statesIneligibleForCard.includes(formik.values.state)) &&
            formik.isSubmitting
        ) {
            formik.setSubmitting(false);
        }
    }, [errors, formik]);

    return (
        <div className={styles.wrapper}>
            <FocusLayout className={styles['focus-layout']}>
                <FormikProvider value={formik}>
                    {!isMobile && isExistingUserWithPassword ? (
                        <FocusHeader
                            leftButtonElement={
                                <BackButton onClick={onBackClicked} />
                            }
                            className={styles['focus-header']}
                        />
                    ) : null}
                    <FocusContent className={styles['focus-content']}>
                        <PartnerHostedOnboardingValueProp
                            isUserInPremiumGroup={isUserInPremiumGroup}
                            welcomeOffer={welcomeOffer}
                        />
                        <TitleSection />
                        <CardApplicationPersonalDetailsForm
                            formik={formik}
                            errors={errors}
                            isUserIdentityVerified={isUserIdentityVerified}
                            dateOfBirth={dateOfBirth}
                            firstName={firstName}
                            lastName={lastName}
                            ssn={ssn}
                            phoneNumber={phoneNumber}
                            isExistingUser={isExistingUser}
                            initialAddressValues={initialAddressValues}
                        />
                        {isMobile ? <CreditScoreDisaclaimer /> : null}
                    </FocusContent>
                    <FocusFooter className={styles['focus-footer']}>
                        <EndToEndTestIdProvider testId='pho-continue-button'>
                            <CardCta
                                Component={CustomButton}
                                mode='dark'
                                loading={formik.isSubmitting}
                                type='submit'
                                onClick={() => {
                                    if (addressHasErrors(formik.errors)) {
                                        setShowAddressSuggestions(false);
                                    } else {
                                        formik.submitForm();
                                    }
                                }}
                            >
                                Continue
                            </CardCta>
                        </EndToEndTestIdProvider>
                        <DisclaimerSection partnerCode={partnerCode} />
                    </FocusFooter>
                </FormikProvider>
                {isModalOpen ? (
                    <IneligibleStateModal
                        onClickEditAddress={() => {
                            setIsModalOpen(false);
                            analyticsOnEditAddressIneligibleStateModal();
                        }}
                        onClose={() => {
                            setIsModalOpen(false);
                            analyticsOnCloseIneligibleStateModal();
                        }}
                        onMount={() => analyticsOnOpenIneligibleStateModal()}
                    />
                ) : null}
                {isEmailModalOpen ? (
                    <EmailMisspelledModal
                        original={formik.values.email}
                        suggestions={emailSuggestions}
                        onClose={() => setIsEmailModalOpen(false)}
                        onChoosingSuggestedEmail={(emailValue) => {
                            formik.setFieldValue('email', emailValue);
                            processSubmit(formik.values);
                        }}
                    />
                ) : null}
                {isExistingUserModalOpen ? (
                    <PartnerOnboardingExistingUserModal
                        className={animatedModalClassName}
                        onClose={() => setIsExistingUserModalOpen(false)}
                        firstName={
                            existingUserFirstName || formik.values.firstName
                        }
                        matchMethod={matchMethod}
                    />
                ) : null}
                {isConfirmAddressModalOpen ? (
                    <CardApplicationConfirmAddress
                        street1={formik.values.street1}
                        street2={formik.values.street2}
                        city={formik.values.city}
                        state={formik.values.state}
                        zipcode={formik.values.zipcode}
                        onClose={() => {
                            setIsConfirmAddressModalOpen(false);
                            onConfirmAddressModalClose();
                        }}
                        onContinue={() => {
                            formik.setSubmitting(true);
                            setIsConfirmAddressModalOpen(false);
                            onConfirmAddressModalContinue(formik.values);
                        }}
                        isLoading={formik.isSubmitting}
                    />
                ) : null}
            </FocusLayout>
            {!isMobile ? <CreditScoreDisaclaimer /> : null}
        </div>
    );
};

export default CardApplicationPersonalDetails;
